import { ProjectStatusesEnum, PropertyTransactionTypesEnum } from "dash-common";
import styled from "styled-components";
import {
  Button,
  FilterContext,
  Flex,
  InputSearch,
  SelectPrice,
  Box,
  Select,
  useScreenResolution,
  SearchContext,
  FormProvider,
  IPriceRange,
  localStorage,
} from "dash-ui";
import camelCase from "lodash/camelCase";
import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { debounce } from "lodash";

import AdvancedFilterDrawer from "containers/properties/AllPropertiesListContainer/components/PropertiesTopBar/components/AdvancedFilterDrawer/AdvancedFilterDrawer";
import { FormFieldsList } from "containers/properties/AllPropertiesListContainer/components/PropertiesTopBar/components/AdvancedFilterDrawer/constants/constants";
import { useGetUniqPropertiesMarketsQuery } from "gql/generated.graphql";

const ContainerWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.space * 16}px;
  align-items: center;
  flex-direction: row;

  @media ${({ theme }) => `${theme.devices.tablet}, ${theme.devices.laptop}`} {
    background-color: transparent;
    align-items: flex-start;
    flex-direction: column;
  }
`;

const MobileContainerWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.space * 16}px;
`;

const PropertiesTopBar = () => {
  const { t } = useTranslation("pages");
  const { setFilterState, state, resetFilterState } = useContext(FilterContext);
  const { setSearchSlug } = useContext(SearchContext);
  const [isFilterOpened, setIsFilterOpened] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { data } = useGetUniqPropertiesMarketsQuery();
  const marketOptions = useMemo(() => {
    const uniqAmenities = data?.getUniqPropertiesMarkets || [];

    return uniqAmenities.map(({ name, _id }) => ({
      value: _id,
      label: name,
    }));
  }, [data]);

  // TODO: Enable only by owner request, for now disabled.
  const isEmptyFilter = true; // every(state, isEmpty);
  const {
    devices: { isMobile },
  } = useScreenResolution();

  const setSearch = useDebouncedCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchSlug(event.target.value);
    },
    300
  );

  const changeFilterPrice = debounce((value: IPriceRange) => {
    setFilterState("prices", value);
  }, 1500);

  // Sets initial transaction type state
  useEffect(() => {
    if (!state.transactionType) {
      const localStorageTransactionType =
        localStorage.getTransactionTypeUserDefault();
      const localTransactionType =
        localStorageTransactionType &&
        PropertyTransactionTypesEnum[localStorageTransactionType];

      setFilterState(
        "transactionType",
        localTransactionType ?? PropertyTransactionTypesEnum.FOR_SALE
      );
    }
  }, []);

  const handleTransactionTypeChange = (value) => {
    setFilterState("transactionType", value);
    localStorage.setTransactionTypeUserDefault(value);
  };

  return (
    <Box w="100%">
      {!isEmptyFilter && (
        <Flex w="100%" my={3} justifyContent="flex-end">
          <Button
            variation="flat"
            icon="close"
            h="fit-content"
            colorVariation="danger"
            minWidth="fit-content"
            onClick={resetFilterState}
          >
            {t("propertyPage.clearFilter")}
          </Button>
        </Flex>
      )}
      <ContainerWrapper
        w="100%"
        mt={isEmptyFilter ? 6 : 0}
        py={3.5}
        px={[4, 0, 0, 4]}
      >
        <Flex w={["100%", "100%", "100%", "80%"]} mr={[3, 2, 3, 3]}>
          <FormProvider fieldsList={FormFieldsList}>
            <AdvancedFilterDrawer
              isOpen={isFilterOpened}
              onRequestClose={() => setIsFilterOpened(false)}
            />
          </FormProvider>
          <MobileContainerWrapper p={[0, 2, 2, 0]} w="100%">
            <InputSearch
              inputVariation="outline"
              onClear={() => {
                setSearchSlug("");
                setSearchValue("");
              }}
              placeholder={t("propertyPage.propertySearchPlaceholder") || ""}
              w="100%"
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value);
                setSearch(event);
              }}
            />
          </MobileContainerWrapper>
        </Flex>

        <MobileContainerWrapper
          p={[0, 2, 2, 0]}
          mt={[0, 1.5, 1.5, 0]}
          w={["fit-content", "100%", "100%", "100%"]}
          alignItems="center"
        >
          {!isMobile && (
            <Select
              w={[124, "100%", "100%", 124]}
              mr={3}
              isSearchable={false}
              isButtonSelect
              placeholder={
                state?.transactionType
                  ? t(
                      `forms:labels.propertyTransactionTypes.${camelCase(
                        state?.transactionType
                      )}`
                    )
                  : t(
                      `forms:labels.propertyTransactionTypes.${camelCase(
                        PropertyTransactionTypesEnum.FOR_SALE
                      )}`
                    )
              }
              onChanged={handleTransactionTypeChange}
              options={Object.values(PropertyTransactionTypesEnum).map(
                (item) => ({
                  label: t(
                    `forms:labels.propertyTransactionTypes.${camelCase(item)}`
                  ),
                  value: item,
                })
              )}
              value={state.transactionType}
            />
          )}

          {!isMobile && (
            <Select
              w={[134, "100%", "100%", 134]}
              mr={3}
              placeholder={t("propertyPage.allMarket") || ""}
              isButtonSelect
              isMulti
              onChanged={(_, values) => setFilterState("marketAreaIds", values)}
              options={marketOptions}
            />
          )}

          {!isMobile && (
            <Select
              w={[182, 182, 182, 182]}
              isSearchable={false}
              isButtonSelect
              placeholder={t("propertyPage.allPropertyStatus") || ""}
              isMulti
              isClearable
              onChanged={(_, values) =>
                setFilterState("projectStatuses", values)
              }
              options={Object.values(ProjectStatusesEnum).map((item) => ({
                label: t(`forms:labels.projectStatuses.${camelCase(item)}`),
                value: item,
              }))}
              value={state.buildingTypes}
            />
          )}
          {!isMobile && (
            <SelectPrice
              mx={3}
              w={[134, "100%", "100%", 134]}
              isButtonSelect
              placeholder={t("propertyPage.priceRange") || ""}
              onChange={changeFilterPrice}
              defaultValue={state.prices}
              isRent={
                state.transactionType === PropertyTransactionTypesEnum.FOR_RENT
              }
            />
          )}

          <Button
            w={["fit-content", "100%", "fit-content", "fit-content"]}
            iconRight="setting2"
            semibold
            variation="outline"
            whiteSpace="nowrap"
            iconProps={{ size: 20 }}
            borderRadius={48}
            px={3}
            ml={[2, 0, 0, 0]}
            onClick={() => setIsFilterOpened(true)}
          >
            {!isMobile && t("propertyPage.advanced")}
          </Button>
        </MobileContainerWrapper>
      </ContainerWrapper>
    </Box>
  );
};

export default PropertiesTopBar;
